/**
 * Minimal API client for ICF Progress endpoints
 * - Adds /api prefix
 * - Attaches auth token
 * - Parses JSON and throws on non-2xx
 */
import {
  API_BASE_URL,
  USE_COOKIES,
  getAccessToken,
  refreshSession,
  clearStoredTokens,
  notifyUnauthorized,
  createIdempotencyKey,
} from './api';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ApiRequestOptions<TBody = any> {
  endpoint: string; // without /api prefix, e.g. '/icf-progress/dashboard'
  method?: HttpMethod;
  data?: TBody;
  params?: Record<string, any>; // query parameters for GET requests
  headers?: Record<string, string>;
  skipAuthRetry?: boolean;
  /** 'blob' returns the raw body as a Blob (file downloads such as CSV export). */
  responseType?: 'json' | 'blob';
}

const RATE_LIMIT_COOLDOWNS: Record<string, number> = {};
const pendingGetRequests = new Map<string, Promise<any>>();

function makeRequestKey(method: HttpMethod, url: string) {
  return `${method.toUpperCase()}:${url}`;
}

function parseRetryAfter(response: Response): number {
  const header = response.headers.get('retry-after');
  if (!header) return 15000;

  const seconds = Number(header);
  if (!Number.isNaN(seconds) && Number.isFinite(seconds)) {
    return Math.max(seconds * 1000, 0);
  }

  const dateValue = Date.parse(header);
  if (!Number.isNaN(dateValue)) {
    return Math.max(dateValue - Date.now(), 0);
  }

  return 15000;
}

export async function apiRequest<TResponse = any, TBody = any>(opts: ApiRequestOptions<TBody>): Promise<TResponse> {
  const { endpoint, method = 'GET', data, params, headers = {} } = opts;
  const baseUrl = `${API_BASE_URL}/api${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  const url = params && Object.keys(params).length > 0
    ? `${baseUrl}?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null).map(([k, v]) => [k, String(v)]))
      ).toString()}`
    : baseUrl;
  const token = USE_COOKIES ? null : await getAccessToken();
  const requestKey = makeRequestKey(method, url) + (opts.responseType === 'blob' ? ':blob' : '');
  const now = Date.now();

  const cooldownUntil = RATE_LIMIT_COOLDOWNS[requestKey];
  if (cooldownUntil && cooldownUntil > now) {
    throw new Error('Rate limit active. Please try again in a moment.');
  }

  const shouldSendBody =
    !(method === 'GET' || method === 'DELETE') || (method === 'DELETE' && data !== undefined);

  // Build headers; only add Content-Type when sending a JSON body
  const baseHeaders: Record<string, string> = {
    ...(shouldSendBody ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...headers,
  };
  const isStripeMutation = method === 'POST' && (
    /^\/billing\/(checkout|portal|change-plan|cancel|resume)$/.test(endpoint) ||
    /^\/billing\/schools\/[^/]+\/(admin-checkout|plan-selection-checkout|plan-checkout|update-seat-capacity)$/.test(endpoint) ||
    endpoint === '/credits/checkout' ||
    endpoint === '/transcriptions/minute-packs/checkout'
  );
  if (isStripeMutation && !baseHeaders['Idempotency-Key'] && !baseHeaders['idempotency-key']) {
    baseHeaders['Idempotency-Key'] = createIdempotencyKey();
  }

  if (method === 'GET') {
    const inflight = pendingGetRequests.get(requestKey);
    if (inflight) {
      return inflight as Promise<TResponse>;
    }
  }

  const requestPromise = (async () => {
    const response = await fetch(url, {
      method,
      headers: baseHeaders,
      credentials: USE_COOKIES ? 'include' : 'same-origin',
      body: shouldSendBody ? JSON.stringify(data ?? {}) : undefined,
    });

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!response.ok) {
      if (response.status === 401) {
        if (!opts.skipAuthRetry) {
          if (method === 'GET') {
            pendingGetRequests.delete(requestKey);
          }
          const outcome = await refreshSession();
          if (outcome === 'unavailable') {
            // Auth backend blip (503 from /auth/refresh): keep the session and
            // let the caller retry later instead of logging the user out.
            const error = new Error('Service temporarily unavailable') as Error & { status: number };
            error.status = 503;
            throw error;
          }
          if (outcome === 'refreshed') {
            // Retry with the caller's headers so the recursive call re-reads
            // the refreshed token; baseHeaders still carries the stale
            // Authorization, which would win the spread and 401 again (FE-14).
            // Keep a generated Idempotency-Key: the retry is the same request.
            const retryHeaders: Record<string, string> = { ...headers };
            if (baseHeaders['Idempotency-Key'] && !headers['Idempotency-Key'] && !headers['idempotency-key']) {
              retryHeaders['Idempotency-Key'] = baseHeaders['Idempotency-Key'];
            }
            return await apiRequest({ ...opts, headers: retryHeaders, skipAuthRetry: true });
          }
        }
        await clearStoredTokens();
        notifyUnauthorized();
        throw new Error('Unauthorized');
      }

      if (response.status === 429) {
        RATE_LIMIT_COOLDOWNS[requestKey] = Date.now() + parseRetryAfter(response);
      }

      let message = `HTTP ${response.status}`;
      let detail: unknown;
      try {
        const err = isJson ? await response.json() : await response.text();
        detail = err && typeof err === 'object' ? err.detail : undefined;
        // A structured detail ({ code, message }) contributes its message as
        // text; the object itself stays on `error.detail` for callers.
        const detailText = detail && typeof detail === 'object' ? (detail as { message?: string }).message : detail;
        message = (err && (err.message || detailText)) || message;
      } catch {}

      // Expose the HTTP status so callers can branch (403 forbidden, 404 not deployed)
      // without parsing the message. `Error` shape is unchanged for existing callers.
      const error = new Error(message) as Error & { status: number; detail?: unknown };
      error.status = response.status;
      if (detail !== undefined) error.detail = detail;
      throw error;
    }

    if (RATE_LIMIT_COOLDOWNS[requestKey]) {
      delete RATE_LIMIT_COOLDOWNS[requestKey];
    }

    if (opts.responseType === 'blob') {
      return (await response.blob()) as unknown as TResponse;
    }

    return (isJson ? await response.json() : (await response.text() as any)) as TResponse;
  })();

  if (method === 'GET') {
    pendingGetRequests.set(requestKey, requestPromise);
    // Do not ignore the Promise returned by finally(): when requestPromise
    // rejects, that derived Promise rejects too and becomes an unhandled
    // rejection even if the caller catches the original request. Supplying
    // both handlers keeps cleanup non-throwing for success and failure.
    void requestPromise.then(
      () => pendingGetRequests.delete(requestKey),
      () => pendingGetRequests.delete(requestKey),
    );
  }

  return requestPromise;
}
